/*
 * Shared pieces for the prototype's stand-in API (see index.js).
 *
 * Each area file exports a list of routes: [method, path, handler]. The path is
 * written the way app/src/api/** calls it ("tenant/roles/:id"); the handler gets
 * { params, query, body, config } and returns [status, json] like the real API.
 */

/** The API's envelope (api/app/Helpers/api_response.php). */
export const ok = (data = null, message = "Success", code = 200) => [code, { status: "success", code, message, data, errors: null }];
export const created = (data = null, message = "Created") => ok(data, message, 201);
export const fail = (message = "Error", errors = null, code = 400) => [code, { status: "error", code, message, data: null, errors }];
export const notFound = (message = "Not found in the sample data.") => fail(message, null, 404);
export const invalid = (errors, message = "Validation failed") => fail(message, errors, 422);

/** A Laravel length-aware page: what ->paginate() serialises to. */
export const paginate = (items, query = {}, perPageDefault = 25) => {
  const perPage = Math.max(1, Number(query.per_page) || perPageDefault);
  const total = items.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(Math.max(1, Number(query.page) || 1), lastPage);
  const start = (current - 1) * perPage;
  const data = items.slice(start, start + perPage);

  return {
    current_page: current,
    data,
    first_page_url: "?page=1",
    from: total ? start + 1 : null,
    last_page: lastPage,
    last_page_url: `?page=${lastPage}`,
    links: [],
    next_page_url: current < lastPage ? `?page=${current + 1}` : null,
    path: "",
    per_page: perPage,
    prev_page_url: current > 1 ? `?page=${current - 1}` : null,
    to: total ? start + data.length : null,
    total,
  };
};

/** The short { current_page, last_page, total, per_page } some screens are given beside their rows. */
export const pageMeta = (page) => ({
  current_page: page.current_page,
  last_page: page.last_page,
  total: page.total,
  per_page: page.per_page,
});

/** Case-insensitive "does any of these fields contain the search". */
export const matches = (search, ...fields) => {
  const q = String(search ?? "").trim().toLowerCase();
  return !q || fields.some((f) => String(f ?? "").toLowerCase().includes(q));
};

export const nextId = (list) => list.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;

// Sample timestamps are relative to now so the prototype never goes stale.
export const hoursAgo = (n) => new Date(Date.now() - n * 36e5).toISOString();
export const daysAgo = (n) => hoursAgo(n * 24);
export const now = () => new Date().toISOString();

/** The request body: JSON is parsed; FormData is read into a plain object. */
export const readBody = (data) => {
  if (!data) return {};
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const out = {};
    data.forEach((value, key) => {
      out[key] = value instanceof File ? { name: value.name, size: value.size } : value;
    });
    return out;
  }
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return data;
};

/** "tenant/roles/:id" → a pattern matching both "tenant/roles/7" and "/api/v1/tenant/roles/7". */
export const compile = (path) => {
  const names = [];
  const source = path
    .replace(/[.+*?^$()[\]{}|\\]/g, "\\$&")
    .replace(/:(\w+)/g, (_, name) => {
      names.push(name);
      return "([^/?#]+)";
    });
  return { regex: new RegExp(`^/?(?:api/v1/)?${source}/?$`), names };
};

/** Which side the prototype session is on: the address decides, as in stores/session.js. */
export const portal = () => (/^(admin\.|[a-z0-9]+-admin\.)/.test(globalThis.location?.hostname ?? "") ? "admin" : "tenant");
